import { useId, type KeyboardEvent, type ReactNode } from 'react'
import { Icon } from './Icon'

/*
 * Wrappers of the multitrack cards of the system (design/system 1792822047-b4d1: InputTracks,
 * TrackSelection; 10-code.md "the class names are the contract"). Markup only, no texts of their
 * own: callers pass them through i18n.ts.
 */

const cx = (...c: (string | false | undefined | null)[]) => c.filter(Boolean).join(' ')

/** Attribute tag (.sx-tag): a neutral fact about an object — 9:16, HDR, Voice, Beta. Never a state. */
export function Tag({ children, className, ...rest }: { children: ReactNode; className?: string; 'data-testid'?: string; title?: string }) {
  return <span className={cx('sx-tag', className)} {...rest}>{children}</span>
}

/** Inline note (.sx-note): the consequence of a change, next to the control. `warn` adds the state colour and icon. */
export function Note({ state, children, id, role, ...rest }: { state?: 'warn' | 'fail'; children: ReactNode; id?: string; role?: 'status' | 'alert'; 'data-testid'?: string }) {
  return (
    <div className="sx-note" data-state={state} id={id} role={role} {...rest}>
      <Icon name={state === 'fail' ? 'state-fail' : state === 'warn' ? 'state-warn' : 'state-idle'} size={16} />
      <span>{children}</span>
    </div>
  )
}

export interface Option<T extends string | number> {
  value: T
  /** mono index (V0, A1) */
  ix?: ReactNode
  /** description: what the eye checks */
  label: ReactNode
  /** attribute tags after the description */
  tags?: ReactNode
  /** right column: codec or bitrate (mono) */
  right?: ReactNode
  /** unavailable: shown, not choosable, with the reason as text under it */
  why?: ReactNode
  /** an available option's description under it, in the same `.why` line (VT-08) */
  desc?: ReactNode
  testId?: string
}

/**
 * Option list (.sx-options > .sx-opt): a radiogroup that replaces a select when there are ≤ 6
 * choices and some can be unavailable. Arrow keys move the choice over available options;
 * an unavailable one keeps its reason under it (aria-describedby), no tooltips.
 */
export function OptionList<T extends string | number>({ value, options, onChange, label, labelledBy, disabled }: {
  value: T
  options: Option<T>[]
  onChange: (v: T) => void
  label?: string
  labelledBy?: string
  /** the whole list (saving) */
  disabled?: boolean
}) {
  const base = useId()
  const ok = options.filter((o) => o.why === undefined)
  const pick = (o: Option<T>) => !disabled && o.why === undefined && onChange(o.value)
  const key = (e: KeyboardEvent<HTMLDivElement>, o: Option<T>) => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault()
      pick(o)
      return
    }
    const d = e.key === 'ArrowDown' || e.key === 'ArrowRight' ? 1 : e.key === 'ArrowUp' || e.key === 'ArrowLeft' ? -1 : 0
    if (!d || !ok.length || disabled) return
    e.preventDefault()
    const at = Math.max(0, ok.findIndex((x) => x.value === o.value))
    const next = ok[(at + d + ok.length) % ok.length]!
    onChange(next.value)
    ;(e.currentTarget.parentElement?.children[options.indexOf(next)] as HTMLElement | undefined)?.focus()
  }
  const focusable = options.some((o) => o.value === value && o.why === undefined) ? value : ok[0]?.value
  return (
    <div className="sx-options" role="radiogroup" aria-label={label} aria-labelledby={labelledBy} aria-disabled={disabled || undefined}>
      {options.map((o, i) => {
        const off = o.why !== undefined
        const why = off || o.desc !== undefined ? `${base}-why-${i}` : undefined
        return (
          <div key={String(o.value)} className="sx-opt max-md:min-h-[44px]" role="radio" data-testid={o.testId}
            aria-checked={o.value === value} aria-disabled={off || disabled || undefined} aria-describedby={why}
            tabIndex={o.value === focusable && !off && !disabled ? 0 : -1}
            onClick={() => pick(o)} onKeyDown={(e) => key(e, o)}>
            <span className="rd" />
            <span className="mn">{o.ix !== undefined && <span className="ix">{o.ix}</span>}{o.label}{o.tags}</span>
            <span className="rt">{o.right}</span>
            {off && <span className="why" id={why}>{o.why}</span>}
            {!off && o.desc !== undefined && <span className="why" id={why}>{o.desc}</span>}
          </div>
        )
      })}
    </div>
  )
}

/** Checkbox (.sx-check): a setting saved with Save (a toggle is only for what applies at once). */
export function Check({ checked, onChange, label, hint, disabled, testId }: { checked: boolean; onChange: (v: boolean) => void; label: ReactNode; hint?: ReactNode; disabled?: boolean; testId?: string }) {
  const id = useId()
  const flip = () => !disabled && onChange(!checked)
  return (
    <div className="sx-check" role="checkbox" aria-checked={checked} aria-disabled={disabled || undefined} tabIndex={disabled ? -1 : 0}
      aria-describedby={hint ? `${id}-hint` : undefined} data-testid={testId}
      onClick={flip} onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); flip() } }}>
      <span className="bx"><Icon name="check" size={12} /></span>
      <span className="lb">{label}</span>
      {hint && <span className="hint" id={`${id}-hint`}>{hint}</span>}
    </div>
  )
}

export interface TrackRow {
  key: string
  /** V0, A1 */
  ix: string
  /** read by screen readers instead of the index ("Video track 1") */
  ixLabel: string
  main: ReactNode
  sub: ReactNode
  right?: ReactNode
  state?: 'ignored' | 'ended'
  testId?: string
}

/** Track list (.sx-tracks): group headers with counts, 40 px rows; ignored / ended rows say why in words. */
export function TrackList({ groups, label, testId }: { groups: { title: ReactNode; count: ReactNode; rows: TrackRow[] }[]; label: string; testId?: string }) {
  return (
    <div className="sx-tracks" role="list" aria-label={label} data-testid={testId}>
      {groups.filter((g) => g.rows.length).map((g, gi) => (
        <div key={gi} role="presentation" style={{ display: 'contents' }}>
          <div className="grp" role="presentation">{g.title} <b>{g.count}</b></div>
          {g.rows.map((r) => (
            <div key={r.key} className="sx-track" role="listitem" data-state={r.state} data-testid={r.testId}>
              <span className="ix" aria-label={r.ixLabel}>{r.ix}</span>
              <span className="mn">{r.main}</span>
              <span className="rt">{r.right}</span>
              <span className="sb">{r.sub}</span>
            </div>
          ))}
        </div>
      ))}
    </div>
  )
}
